import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { formValueSelector } from 'redux-form';
import dateUtils from 'helpers/dateUtils';
import Column from 'components/ui/show/Column';
import filterMatchTypes from 'components/list/Filters/filterMatchTypes';
import InnerColumn from 'components/ui/show/InnerColumn';
import LoadingCard from 'components/loading/LoadingCard';
import NoResults from 'components/form/styling/NoResults';
import Pagination from 'components/list/Pagination/Pagination';
import ProductReservationsList from './list/ProductReservationsList';
import ProductReservationsOthersList from './list/ProductReservationsOthersList';
import { fetchReservationsRequest } from 'http/requests/reservations/reservationsRequests';
import { showNotification } from 'store/actions/uiActions';
import paginationHelper from 'helpers/pagination';
import translate from 'components/hoc/translate';
import utils from 'helpers/utils';

const SOURCE_ID = 'id';
const DESTINATION_ARRIVAL_DATE = 'destination_arrival_date';
const DESTINATION_DEPARTURE_DATE = 'destination_departure_date';
const DESTINATION_ARRIVAL_DATE_UNTIL = 'destination_arrival_date_until';
const DESTINATION_DEPARTURE_DATE_FROM = 'destination_departure_date_from';

class ProductReservationsContainer extends Component {
    constructor(props) {
        super(props);

        const { source } = props;

        this.state = {
            loading: false,
            offset: 0,
            page: 1,
            reservations: null,
            size: 96,
            sort_direction: 'asc',
            sort_by: source || DESTINATION_ARRIVAL_DATE,
            total: 0,
        };
    }

    componentDidUpdate(prevProps, prevState) {
        const {
            query,
        } = this.props;

        const {
            offset,
        } = this.state;

        const isDifferent = !utils.isEmpty(utils.diffObjects(prevProps.query, query));

        if (isDifferent || offset !== prevState.offset) {
            this.fetchProductReservations();
        }
    }

    fetchProductReservations = () => {
        const {
            id,
            query,
            showErroNotification,
            source,
        } = this.props;

        const {
            offset,
            size,
            sort_direction,
            sort_by,
        } = this.state;

        this.setState({
            loading: true,
            total: 0,
        });

        const params = {
            filter: {
                product_id: {
                    matchType: filterMatchTypes.equals,
                    value: id,
                },
            },
        };

        const fromDateString = `${ query.from } 00:00:00`;
        const toDateString = `${ query.to } 23:59:59`;

        if (source) {
            params.filter[source] = {
                matchType: filterMatchTypes.range,
                value: [
                    fromDateString,
                    toDateString,
                ],
            };
        } else {
            params.filter[DESTINATION_ARRIVAL_DATE_UNTIL] = {
                matchType: filterMatchTypes.equals,
                value: fromDateString,
            };

            params.filter[DESTINATION_DEPARTURE_DATE_FROM] = {
                matchType: filterMatchTypes.equals,
                value: toDateString,
            };
        }

        const payload = {
            cancelled: 0,
            offset: offset,
            size: size,
            sort_direction: sort_direction,
            sort_by: sort_by,
            ...utils.buildFilterObject(params.filter),
        };

        return fetchReservationsRequest(payload).then((response) => {
            setTimeout(() => {
                this.setState({
                    loading: false,
                    reservations: response.results,
                    total: response.total,
                });
            }, 500);
        }, (error) => {
            const {
                message,
            } = error.response;

            this.setState({
                loading: false,
            });

            showErroNotification(message);
        });
    }

    setPage = (newPage) => {
        const { size } = this.state;

        this.setState({
            page: newPage,
            offset: paginationHelper.generateNewOffset(newPage, size),
        });
    }

    render() {
        const {
            query,
            source,
            translate,
        } = this.props;

        const {
            loading,
            offset,
            page,
            reservations,
            size,
            total,
        } = this.state;

        const translationSource = source || SOURCE_ID;

        if (!reservations) {
            return (
                <Column title={ translate(`resources.products.occupancy.reservations.${ translationSource }.title`, { count: total }) }>
                    <InnerColumn >
                        <NoResults label={ `resources.products.occupancy.reservations.${ translationSource }.no_results` } />
                    </InnerColumn>
                </Column>
            );
        }

        if (loading) {
            return (
                <Column title={ translate(`resources.products.occupancy.reservations.${ translationSource }.title`, { count: total }) }>
                    <InnerColumn >
                        <LoadingCard />
                    </InnerColumn>
                </Column>
            );
        }

        const showPagination = (total / size) > 1;

        const isDateRange = !dateUtils.isSameDay(dateUtils.stringToDate(query.from), dateUtils.stringToDate(query.to));

        return (
            <Column title={ translate(`resources.products.occupancy.reservations.${ translationSource }.title`, { count: total || 0 }) }>
                <InnerColumn>
                    { source &&
                        <ProductReservationsList
                            isDateRange={ isDateRange }
                            reservations={ reservations }
                            source={ source }
                        />
                    }

                    { !source &&
                        <ProductReservationsOthersList
                            reservations={ reservations }
                        />
                    }

                    { showPagination &&
                        <Pagination
                            currentPage={ page }
                            offset={ offset }
                            setPage={ this.setPage }
                            size={ size }
                            total={ total }
                        />
                    }
                </InnerColumn>
            </Column>
        );
    }
}

ProductReservationsContainer.propTypes = {
    id: PropTypes.string.isRequired,
    query: PropTypes.shape({
        from: PropTypes.string,
        to: PropTypes.string,
    }).isRequired,
    showErroNotification: PropTypes.func.isRequired,
    source: PropTypes.oneOf([
        DESTINATION_ARRIVAL_DATE,
        DESTINATION_DEPARTURE_DATE,
    ]),
    translate: PropTypes.func.isRequired,
};

const mapStateToProps = (state, props) => {
    const { form } = props;

    const formValuesSelector = formValueSelector(form);

    return {
        query: formValuesSelector(state, 'from', 'to'),
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        showErroNotification: (message) => {
            dispatch(showNotification(message, 'warning'));
        },
    };
};

const enhance = compose(
    connect(
        mapStateToProps,
        mapDispatchToProps,
    ),
    translate
);

export default enhance(ProductReservationsContainer);


// WEBPACK FOOTER //
// ./src/pages/products/productOccupancy/reservations/ProductReservationsContainer.js